import React, { useState, useRef } from 'react';
import {
  User, Mail, Phone, Shield, Camera, Trash2, Loader, KeyRound,
  Save, Clock, CalendarDays, Store, ShieldCheck, Copy, CheckCircle2,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PhoneInput from '../../components/common/PhoneInput';
import { resolveUploadUrl, initiales } from '../../utils/media';
import toast from 'react-hot-toast';

// Libellé lisible d'un rôle.
// ⚠️ Toute table indexée par le rôle DOIT couvrir les 5 rôles existants, sinon
// un rôle oublié se dégrade en silence (cf. le bug de BottomNav, où l'oubli de
// 'gerant_vendeur' faisait disparaître toute la navigation mobile).
// Rôles : admin | proprietaire | gerant_vendeur | vendeur | consultation
// ('employe' = ancien nom de 'gerant_vendeur', renommé par la migration 023 ;
//  gardé en alias au cas où une ligne n'aurait pas été migrée.)
const ROLE_LABELS = {
  admin: 'Administrateur',
  proprietaire: 'Propriétaire',
  gerant_vendeur: 'Gérant / Vendeur',
  employe: 'Gérant / Vendeur',
  vendeur: 'Vendeur terrain',
  consultation: 'Consultation (lecture seule)',
};

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

// ── Carte de section ──────────────────────────────────────────
function Carte({ icon: Icon, titre, sousTitre, children, couleur }) {
  return (
    <div className="bg-gray-900 rounded-2xl border border-gray-800 p-5 md:p-6">
      <div className="flex items-start gap-3 mb-5">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: (couleur || '#64748b') + '22', color: couleur || '#94a3b8' }}
        >
          <Icon size={20} />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-100">{titre}</h2>
          {sousTitre && <p className="text-xs text-gray-500 mt-0.5">{sousTitre}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Champ({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors';

// ── Bloc « Double authentification (2FA) » ────────────────────
function Securite2FA({ accent }) {
  const { user, refreshUser } = useAuth();
  const actif = user?.deux_facteurs_actif === true;
  const [etat, setEtat] = useState('idle');      // idle | setup | codes
  const [secret, setSecret] = useState('');
  const [uri, setUri] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [pwdDisable, setPwdDisable] = useState('');

  const copier = (txt) => {
    try { navigator.clipboard.writeText(txt); toast.success('Copié'); } catch { /* ignore */ }
  };

  const demarrer = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/2fa/setup');
      setSecret(data.secret || '');
      setUri(data.uri || '');
      setCode('');
      setEtat('setup');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    } finally { setLoading(false); }
  };

  const verifier = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return toast.error('Saisis le code à 6 chiffres.');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/2fa/verify', { code });
      setCodes(data.codesSecours || []);
      setEtat('codes');
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Code invalide.');
    } finally { setLoading(false); }
  };

  const desactiver = async (e) => {
    e.preventDefault();
    if (!pwdDisable) return toast.error('Mot de passe requis.');
    setLoading(true);
    try {
      await api.post('/auth/2fa/disable', { password: pwdDisable });
      toast.success('Double authentification désactivée.');
      setShowDisable(false); setPwdDisable('');
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    } finally { setLoading(false); }
  };

  const secretFormate = secret.replace(/(.{4})/g, '$1 ').trim();

  return (
    <Carte
      icon={ShieldCheck}
      titre="Double authentification (2FA)"
      sousTitre="Ajoute un code à usage unique à ta connexion, généré par une application d'authentification (Google Authenticator, Authy…)."
      couleur={accent}
    >
      {/* Étape : codes de secours (affichés une seule fois après activation) */}
      {etat === 'codes' ? (
        <div className="max-w-md">
          <div className="flex items-center gap-2 text-green-400 mb-3">
            <CheckCircle2 size={18} /> <span className="font-semibold">2FA activée</span>
          </div>
          <p className="text-sm text-gray-300 mb-2">
            Conserve ces <strong>codes de secours</strong> en lieu sûr. Chacun fonctionne
            <strong> une seule fois</strong> et te permet de te connecter si tu perds ton téléphone.
            <span className="text-yellow-400"> Ils ne seront plus affichés.</span>
          </p>
          <div className="grid grid-cols-2 gap-2 bg-gray-800/60 border border-gray-700 rounded-xl p-3 font-mono text-sm text-gray-100">
            {codes.map((c) => <div key={c} className="text-center tracking-wider">{c}</div>)}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => copier(codes.join('\n'))}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg">
              <Copy size={14} /> Copier les codes
            </button>
            <button onClick={() => { setEtat('idle'); setSecret(''); setCodes([]); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg"
              style={{ backgroundColor: accent, color: '#0f172a' }}>
              J'ai noté mes codes
            </button>
          </div>
        </div>
      ) : etat === 'setup' ? (
        /* Étape : configuration (saisie de la clé + vérification) */
        <form onSubmit={verifier} className="max-w-md space-y-4">
          {uri && (
            <div className="flex flex-col items-center gap-2">
              <div className="bg-white p-3 rounded-xl"><QRCodeSVG value={uri} size={168} /></div>
              <p className="text-xs text-gray-400 text-center">Scanne ce QR code dans ton application d'authentification — ou, si tu ne peux pas scanner, saisis la clé ci-dessous.</p>
            </div>
          )}
          <ol className="text-sm text-gray-300 space-y-1.5 list-decimal list-inside">
            <li>Ouvre ton application d'authentification.</li>
            <li>Scanne le QR code ci-dessus (ou « Ajouter un compte » → « Saisir une clé de configuration »).</li>
            <li>Le nom du compte est ton email.</li>
          </ol>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Clé de configuration</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white font-mono tracking-wider break-all">
                {secretFormate}
              </code>
              <button type="button" onClick={() => copier(secret)}
                className="p-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg shrink-0" title="Copier la clé">
                <Copy size={16} />
              </button>
            </div>
          </div>
          <Champ label="Code à 6 chiffres affiché par l'application">
            <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric" placeholder="000000"
              className={inputCls + ' tracking-[0.4em] text-center'} autoFocus />
          </Champ>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setEtat('idle'); setSecret(''); }}
              className="px-4 py-2.5 text-sm bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-xl">
              Annuler
            </button>
            <button type="submit" disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl disabled:opacity-50"
              style={{ backgroundColor: accent, color: '#0f172a' }}>
              {loading && <Loader size={16} className="animate-spin" />}
              Activer la 2FA
            </button>
          </div>
        </form>
      ) : actif ? (
        /* Statut : activée */
        <div className="max-w-md">
          <div className="flex items-center gap-2 text-green-400 mb-3">
            <CheckCircle2 size={18} /> <span className="font-semibold">Activée</span>
            <span className="text-sm text-gray-400">— un code te sera demandé à chaque connexion.</span>
          </div>
          {showDisable ? (
            <form onSubmit={desactiver} className="space-y-3">
              <Champ label="Confirme avec ton mot de passe pour désactiver">
                <input type="password" value={pwdDisable} onChange={e => setPwdDisable(e.target.value)}
                  className={inputCls} autoComplete="current-password" autoFocus />
              </Champ>
              <div className="flex gap-2">
                <button type="button" onClick={() => { setShowDisable(false); setPwdDisable(''); }}
                  className="px-4 py-2.5 text-sm bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-xl">Annuler</button>
                <button type="submit" disabled={loading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white rounded-xl disabled:opacity-50">
                  {loading && <Loader size={16} className="animate-spin" />} Désactiver la 2FA
                </button>
              </div>
            </form>
          ) : (
            <button onClick={() => setShowDisable(true)}
              className="px-4 py-2.5 text-sm bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-xl border border-gray-700">
              Désactiver la 2FA
            </button>
          )}
        </div>
      ) : (
        /* Statut : non activée */
        <div className="max-w-md">
          <p className="text-sm text-gray-400 mb-4">
            Protège ton compte : même si quelqu'un connaît ton mot de passe, il lui faudra
            aussi le code de ton téléphone pour se connecter.
          </p>
          <button onClick={demarrer} disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl disabled:opacity-50"
            style={{ backgroundColor: accent, color: '#0f172a' }}>
            {loading ? <Loader size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            Activer la 2FA
          </button>
        </div>
      )}
    </Carte>
  );
}

export default function MonProfil() {
  const { user, commerce, refreshUser, couleurIcones } = useAuth();
  const accent = couleurIcones || '#64748b';

  // ── État infos personnelles ──
  const [infos, setInfos] = useState({
    prenom: user?.prenom || '',
    nom: user?.nom || '',
    email: user?.email || '',
    telephone: user?.telephone || '',
  });
  const [savingInfos, setSavingInfos] = useState(false);

  // ── État mot de passe ──
  const [pwd, setPwd] = useState({ ancien: '', nouveau: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);

  // ── État avatar ──
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);

  const photoUrl = resolveUploadUrl(user?.photo_url);
  const ini = initiales(user?.prenom, user?.nom);
  const roleLabel = ROLE_LABELS[user?.role] || user?.role || '';

  // ── Handlers ──
  const handleInfos = e => setInfos(f => ({ ...f, [e.target.name]: e.target.value }));

  const enregistrerInfos = async e => {
    e.preventDefault();
    if (!infos.prenom.trim() || !infos.nom.trim()) {
      return toast.error('Le prénom et le nom sont requis');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(infos.email.trim())) {
      return toast.error('Adresse email invalide');
    }
    setSavingInfos(true);
    try {
      await api.put('/auth/me', {
        prenom: infos.prenom.trim(),
        nom: infos.nom.trim(),
        email: infos.email.trim(),
        telephone: infos.telephone || '',
      });
      await refreshUser();
      toast.success('Profil mis à jour');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingInfos(false);
    }
  };

  const changerMotDePasse = async e => {
    e.preventDefault();
    if (pwd.nouveau !== pwd.confirm) {
      return toast.error('Les nouveaux mots de passe ne correspondent pas');
    }
    if (pwd.nouveau.length < 6) {
      return toast.error('Le mot de passe doit faire au moins 6 caractères');
    }
    setSavingPwd(true);
    try {
      await api.post('/auth/change-password', {
        ancienMotDePasse: pwd.ancien,
        nouveauMotDePasse: pwd.nouveau,
      });
      toast.success('Mot de passe modifié');
      setPwd({ ancien: '', nouveau: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors du changement');
    } finally {
      setSavingPwd(false);
    }
  };

  const choisirPhoto = () => fileRef.current?.click();

  const uploaderPhoto = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';            // permet de re-choisir le même fichier
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      return toast.error('Image trop volumineuse (2 Mo max)');
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      await api.post('/auth/me/photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshUser();
      toast.success('Photo mise à jour');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const supprimerPhoto = async () => {
    setUploadingPhoto(true);
    try {
      await api.delete('/auth/me/photo');
      await refreshUser();
      toast.success('Photo supprimée');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Mon Profil</h1>
        <p className="text-gray-500 text-sm mt-1">Gère tes informations personnelles et la sécurité de ton compte</p>
      </div>

      {/* Carte identité (hero) */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-800 p-5 md:p-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
          {/* Avatar */}
          <div className="relative shrink-0">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={`${user?.prenom} ${user?.nom}`}
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-700"
              />
            ) : (
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold border-2 border-gray-700"
                style={{ backgroundColor: accent + '22', color: accent }}
              >
                {ini}
              </div>
            )}
            <button
              onClick={choisirPhoto}
              disabled={uploadingPhoto}
              title="Changer la photo"
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center text-white shadow-lg disabled:opacity-60"
              style={{ backgroundColor: accent }}
            >
              {uploadingPhoto ? <Loader size={16} className="animate-spin" /> : <Camera size={16} />}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={uploaderPhoto}
              className="hidden"
            />
          </div>

          {/* Identité */}
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-bold text-white">
              {user?.prenom} {user?.nom}
            </h2>
            <div className="flex items-center justify-center sm:justify-start gap-2 mt-1.5">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: accent + '22', color: accent }}
              >
                <Shield size={12} /> {roleLabel}
              </span>
              {commerce?.nom && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-700/50 text-gray-300">
                  <Store size={12} /> {commerce.nom}
                </span>
              )}
            </div>
            <div className="mt-3 space-y-1 text-sm text-gray-400">
              <p className="flex items-center justify-center sm:justify-start gap-2">
                <Mail size={14} /> {user?.email}
              </p>
              {user?.telephone && (
                <p className="flex items-center justify-center sm:justify-start gap-2">
                  <Phone size={14} /> {user.telephone}
                </p>
              )}
            </div>
            {photoUrl && (
              <button
                onClick={supprimerPhoto}
                disabled={uploadingPhoto}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
              >
                <Trash2 size={13} /> Supprimer la photo
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Informations personnelles */}
      <Carte
        icon={User}
        titre="Informations personnelles"
        sousTitre="Ces informations identifient ton compte dans l'application"
        couleur={accent}
      >
        <form onSubmit={enregistrerInfos} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Champ label="Prénom">
              <input name="prenom" value={infos.prenom} onChange={handleInfos} className={inputCls} placeholder="Prénom" />
            </Champ>
            <Champ label="Nom">
              <input name="nom" value={infos.nom} onChange={handleInfos} className={inputCls} placeholder="Nom" />
            </Champ>
          </div>
          <Champ label="Adresse email">
            <input name="email" type="email" value={infos.email} onChange={handleInfos} className={inputCls} placeholder="email@gmail.com" />
          </Champ>
          <Champ label="Téléphone">
            <PhoneInput
              value={infos.telephone}
              onChange={e => setInfos(f => ({ ...f, telephone: e.target.value }))}
            />
          </Champ>
          <button
            type="submit"
            disabled={savingInfos}
            className="inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-xl font-semibold transition disabled:opacity-50"
            style={{ backgroundColor: accent }}
          >
            {savingInfos ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            {savingInfos ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </form>
      </Carte>

      {/* Sécurité */}
      <Carte
        icon={KeyRound}
        titre="Sécurité"
        sousTitre="Change ton mot de passe. L'ancien est demandé pour confirmer ton identité."
        couleur={accent}
      >
        <form onSubmit={changerMotDePasse} className="space-y-4 max-w-md">
          <Champ label="Mot de passe actuel">
            <input type="password" value={pwd.ancien} onChange={e => setPwd(p => ({ ...p, ancien: e.target.value }))} className={inputCls} autoComplete="current-password" />
          </Champ>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Champ label="Nouveau mot de passe">
              <input type="password" value={pwd.nouveau} onChange={e => setPwd(p => ({ ...p, nouveau: e.target.value }))} className={inputCls} autoComplete="new-password" />
            </Champ>
            <Champ label="Confirmer">
              <input type="password" value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} className={inputCls} autoComplete="new-password" />
            </Champ>
          </div>
          <button
            type="submit"
            disabled={savingPwd}
            className="inline-flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-5 py-2.5 rounded-xl font-semibold transition disabled:opacity-50"
          >
            {savingPwd ? <Loader size={16} className="animate-spin" /> : <KeyRound size={16} />}
            {savingPwd ? 'Modification…' : 'Changer le mot de passe'}
          </button>
        </form>

        {/* Infos de sécurité (lecture seule) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 pt-5 border-t border-gray-800">
          <div className="flex items-center gap-3 bg-gray-800/50 rounded-xl px-4 py-3">
            <Clock size={18} className="text-gray-500 shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Dernière connexion</p>
              <p className="text-sm text-gray-200">{formatDate(user?.derniere_connexion)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-gray-800/50 rounded-xl px-4 py-3">
            <CalendarDays size={18} className="text-gray-500 shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Compte créé le</p>
              <p className="text-sm text-gray-200">{formatDate(user?.created_at)}</p>
            </div>
          </div>
        </div>
      </Carte>

      {/* Double authentification (2FA) */}
      <Securite2FA accent={accent} />
    </div>
  );
}
